<script lang="ts" setup>
import { ElTag } from 'element-plus'
import { useElementSize } from '@vueuse/core'
import type { Container, ContainerBadgeInfo } from '~/models/containers'
import { useReturnRulesLatest } from '~/stores/returnRulesLatest'
import { ReceivingRule } from '~/services/apiClient'

const props = defineProps<{
  badges: ContainerBadgeInfo[]
  includeViolationRule?: boolean
  container?: Container
}>()
const MIN_BADGE_WIDTH = 25
const displayBadges = ref([] as ContainerBadgeInfo[])
const overflowBadges = ref([] as ContainerBadgeInfo[])
const holder = ref(null)
const { width } = useElementSize(holder)

const ruleBadge = (container: Container): ContainerBadgeInfo | undefined => {
  const appt = container.booked_appointment
  if (!appt || !container.line || !container.type || !container.terminal) return

  const rule = useReturnRulesLatest().lookupRule(
    container.line,
    container.type,
    container.terminal,
    appt.window_start,
    appt.shift
  )

  if (!rule) return

  const isDual = appt.linked_appointments?.length > 0
  const isSingle = !isDual

  const violates =
    (isSingle && rule.rule !== ReceivingRule.Open) ||
    (isDual && rule.rule === ReceivingRule.Closed)

  if (violates) {
    return {
      message: 'Violates Return Rules',
      type: 'danger',
      tooltip: `Return rule for ${container.line}/${container.type} at ${container.terminal} is "${rule.rule}"`,
    }
  }
}

const allBadges = computed(() => {
  const base = [...props.badges]
  if (
    props.includeViolationRule &&
    props.container &&
    props.container.latest_appointment?.is_empty_in
  ) {
    const extra = ruleBadge(props.container)
    if (extra) base.push(extra)
  }
  return base
})

watchEffect(() => {
  const newBadges = []
  const newOverflowBadges = []
  let usedLength = 0
  const maxWidth = width.value - 3
  for (const badge of allBadges.value) {
    const badgeLength = MIN_BADGE_WIDTH + badge.message.length * 7
    if (usedLength + badgeLength < maxWidth) {
      newBadges.push(badge)
      usedLength += badgeLength
    } else {
      newOverflowBadges.push(badge)
    }
  }
  displayBadges.value = newBadges
  overflowBadges.value = newOverflowBadges
})
</script>

<template>
  <div ref="holder" class="badge-holder w-full">
    <ContainerBadge
      v-for="badge in displayBadges"
      :key="badge.message"
      :badge="badge"
      :container-number="container?.number"
    />
    <el-popover
      v-if="overflowBadges.length"
      trigger="hover"
      :persistent="false"
      :width="200"
    >
      <ContainerBadge
        v-for="badge in overflowBadges"
        :key="badge.message"
        :badge="badge"
        :container-number="container?.number"
        class="my-1 mr-2"
      />

      <template #reference>
        <ElTag size="small" type="info" plain>
          +{{ overflowBadges.length }}
        </ElTag>
      </template>
    </el-popover>
  </div>
</template>

<style lang="scss" scoped>
.badge-holder {
  min-height: 24px;
}
</style>
